import React, { useMemo, useState } from 'react';
import { TitularTooltip } from '@campfire/titular-tooltip';
import { Box, Button, ListItem, Typography, IconButton } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { AddCircle, KeyboardArrowDown, KeyboardArrowUp, Cancel } from '@material-ui/icons';
import { VolunteerProfile } from '../../../../common/AvatarName';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { WaitlistedVolunteersSkeleton } from './WaitlistedVolunteersSkeleton';
import { GetActivitiesConsoleWaitlistedVolunteers_vm_activity_activeActivityWaitlistings as WaitlistType } from './__generated__/GetActivitiesConsoleWaitlistedVolunteers';

const useStyles = makeStyles(() =>
  createStyles({
    itemContainer: {
      '& .itemAction': {
        display: 'none',
      },
      '&:hover': {
        '& .itemAction': {
          display: 'flex',
        },
      },
    },
  })
);
interface Props {
  activityId?: string;
  programId: string;
  searchTerm: string;
  waitlists: WaitlistType[];
  isLoading: boolean;
  onAdd: (waitlistingId: string) => void;
  onRemove: (waitlistingId: string) => void;
}

interface TeamMemberItemProps {
  teamMember: WaitlistType;
}

const TeamMemberItem = (props: TeamMemberItemProps) => {
  const { teamMember } = props;
  const { profile } = teamMember.volunteer;
  const displayName = `${profile.preferredName} ${profile.lastName}`;
  return (
    <Box style={{ display: 'inline-block' }}>
      <Box
        component='li'
        key={teamMember.activityWaitlistingId}
        display='flex'
        flex={1}
        style={{
          marginTop: 8,
          marginBottom: 8,
        }}
      >
        <VolunteerProfile
          avatarUrl={profile.avatarUrl}
          name={displayName}
          noTrim
          userId={profile.userId}
          profileId={profile.profileId}
          lastName={profile.lastName}
          preferredName={profile.preferredName}
        />
      </Box>
    </Box>
  );
};

export const WaitlistedVolunteersDetail = (props: Props) => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const { searchTerm, waitlists, isLoading, onAdd, onRemove } = props;
  const { theme } = useCampfireTheme();

  const filteredWaitlists = useMemo(() => {
    return waitlists.filter((waitlist) => {
      const { profile } = waitlist.volunteer;
      const fullName = `${profile.preferredName} ${profile.lastName}`;
      return fullName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [searchTerm, waitlists]);

  const showingWaitlists = useMemo(() => {
    if (showMore) {
      return filteredWaitlists;
    }
    return filteredWaitlists.slice(0, 3);
  }, [showMore, filteredWaitlists]);

  const classes = useStyles();

  return (
    <Box>
      {isLoading
        ? Array.from(Array(5)).map(() => (
            <ListItem>
              <WaitlistedVolunteersSkeleton />
            </ListItem>
          ))
        : showingWaitlists.map((w) => (
            <Box display='flex' justifyContent='space-between' alignItems='center' className={classes.itemContainer}>
              <TeamMemberItem teamMember={w} />
              <Box display='flex' className='itemAction'>
                <TitularTooltip title='Remove'>
                  <IconButton onClick={() => onRemove(w.activityWaitlistingId)}>
                    <Cancel style={{ marginTop: '2px', color: theme.color.error[900] }} />
                  </IconButton>
                </TitularTooltip>
                <TitularTooltip title='Add to team'>
                  <IconButton onClick={() => onAdd(w.activityWaitlistingId)}>
                    <AddCircle style={{ marginTop: '2px', color: theme.color.secondary.main700 }} />
                  </IconButton>
                </TitularTooltip>
              </Box>
            </Box>
          ))}

      <Box style={{ width: '100%', paddingTop: '12px' }}>
        {waitlists.length > 3 ? (
          <Button
            variant='text'
            style={{
              marginLeft: 'auto',
              marginRight: 'auto',
              padding: '8px 12px',
              boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
              borderRadius: '8px',
              display: 'block',
            }}
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? (
              <Box display='flex'>
                <Typography
                  style={{
                    color: theme.color.grey.neutral400,
                    fontSize: '14px',
                    textTransform: 'none',
                    marginRight: '4px',
                  }}
                >
                  Show Less
                </Typography>
                <KeyboardArrowUp style={{ fontSize: '20px', color: theme.color.grey.neutral400 }} />
              </Box>
            ) : (
              <Box display='flex'>
                <Typography
                  style={{
                    color: theme.color.grey.neutral400,
                    fontSize: '14px',
                    textTransform: 'none',
                    marginRight: '4px',
                  }}
                >
                  Show More
                </Typography>
                <KeyboardArrowDown style={{ fontSize: '20px', color: theme.color.grey.neutral400 }} />
              </Box>
            )}
          </Button>
        ) : (
          ''
        )}
      </Box>
      {!waitlists.length && !isLoading ? (
        <Box textAlign='center' paddingTop='10px' color={theme.color.grey.neutral400}>
          <Typography style={{ fontSize: '13px' }}>
            &#127881; There are no waitlisted volunteers for this activity
          </Typography>
        </Box>
      ) : (
        ''
      )}
    </Box>
  );
};
